import { formatDate } from '@/shared/utils/date';
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react';
import React from 'react';
import { EMPTY_ADDITIONAL_FIELDS, EMPTY_JSON_SECTIONS } from './constants';

type LogDetailsModalProps = {
  visible: boolean;
  onClose: () => void;
  title: string;
  baseData: {
    id: number;
    createdAt: string;
  };
  additionalFields?: Array<{
    label: string;
    value: React.ReactNode;
  }>;
  jsonSections?: Array<{
    title: string;
    data: unknown;
  }>;
};

/**
 * Universal modal component for displaying log details.
 * Can be used for audit logs, activity logs, or any other log type.
 */
export const LogDetailsModal: React.FC<LogDetailsModalProps> = ({
  visible,
  onClose,
  title,
  baseData,
  additionalFields = EMPTY_ADDITIONAL_FIELDS,
  jsonSections = EMPTY_JSON_SECTIONS,
}) => {
  return (
    <CModal visible={visible} onClose={onClose} size="lg">
      <CModalHeader>
        <CModalTitle>{title}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {/* Base information */}
        <div className="row mb-3">
          <div className="col-md-6">
            <strong>ID:</strong> {baseData.id}
          </div>
          <div className="col-md-6">
            <strong>Timestamp:</strong> {formatDate(baseData.createdAt)}
          </div>
        </div>

        {/* Additional fields - grouped by pairs for better layout */}
        {additionalFields.length > 0 && (
          <>
            {Array.from({ length: Math.ceil(additionalFields.length / 2) }, (_, rowIndex) => {
              const firstField = additionalFields[rowIndex * 2];
              const secondField = additionalFields[rowIndex * 2 + 1];

              return (
                <div
                  key={`${firstField.label}-${secondField?.label ?? 'single'}`}
                  className="row mb-3"
                >
                  <div className="col-md-6">
                    <strong>{firstField.label}:</strong> {firstField.value}
                  </div>
                  {secondField ? (
                    <div className="col-md-6">
                      <strong>{secondField.label}:</strong> {secondField.value}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </>
        )}

        {/* JSON sections */}
        {jsonSections.map((section) => (
          <div key={section.title} className="mb-3">
            <h6>{section.title}</h6>
            <pre
              className="bg-light p-3 rounded"
              style={{ fontSize: '0.875rem', maxHeight: '300px', overflow: 'auto' }}
            >
              <code>{JSON.stringify(section.data, null, 2)}</code>
            </pre>
          </div>
        ))}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Close
        </CButton>
      </CModalFooter>
    </CModal>
  );
};
